import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Button,
  ButtonGroup,
  Modal,
  Backdrop,
  Fade,
  Divider,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import useTheme from '../hooks/useTheme';

export default function Feedback() {
  const theme = useTheme();
  const [feedbackData, setFeedbackData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchEmail, setSearchEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // Fetch feedback data from API
  const fetchFeedbackData = async (page = 1) => {
    setLoading(true);
    try {
      const response = await fetch(`https://api.uxhq.com/feedback_sessions?page=${page}&limit=100`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Transform API data to match our component structure
      const transformedData = result.data.map((item, index) => ({
        id: item.report_id,
        email: item.user_email || 'N/A',
        rating: item.rating || 0,
        feedback: item.comment || 'No feedback provided',
        date: item.created_at ? new Date(item.created_at).toLocaleString() : 'N/A'
      }));
      
      setFeedbackData(transformedData);
      setFilteredData(transformedData);
      setPagination(result.pagination);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching feedback data:', error);
      // Fallback to empty data on error
      setFeedbackData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch specific report details by report ID
  const fetchReportDetails = async (reportId) => {
    setReportLoading(true);
    try {
      const response = await fetch(`https://api.uxhq.com/report_by_id/${reportId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      setSelectedReport(result.data);
      setModalOpen(true);
    } catch (error) {
      console.error('Error fetching report details:', error);
      // You could add a toast notification here for better UX
    } finally {
      setReportLoading(false);
    }
  };

  // Handle report ID click
  const handleReportIdClick = (reportId) => {
    fetchReportDetails(reportId);
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedReport(null);
  };

  useEffect(() => {
    fetchFeedbackData(1);
  }, []);

  // Filter data based on email search
  useEffect(() => {
    if (!searchEmail.trim()) {
      setFilteredData(feedbackData);
    } else {
      const filtered = feedbackData.filter(item =>
        item.email.toLowerCase().includes(searchEmail.toLowerCase())
      );
      setFilteredData(filtered);
    }
  }, [searchEmail, feedbackData]);

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        i <= rating ? (
          <StarIcon key={i} sx={{ color: '#ffc107', fontSize: 16 }} />
        ) : (
          <StarBorderIcon key={i} sx={{ color: theme.textSecondaryColor, fontSize: 16 }} />
        )
      );
    }
    return stars;
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return '#4caf50';
    if (rating >= 3) return '#ff9800';
    return '#f44336';
  };

  // Helper function to count words in text
  const countWords = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).length;
  };

  // Helper function to truncate text to 25 words
  const truncateText = (text, maxWords = 10) => {
    if (!text) return '';
    const words = text.trim().split(/\s+/);
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + '...';
  };

  // Toggle row expansion
  const toggleRowExpansion = (rowId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    setExpandedRows(newExpandedRows);
  };

  return (
    <Box sx={{ 
      p: 3, 
      background: theme.backgroundColor, 
      minHeight: '100vh',
      color: theme.textColor 
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ 
          fontWeight: 'bold', 
          color: theme.textColor,
          mb: 1
        }}>
          FEEDBACK
        </Typography>
      </Box>

      {/* Search */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
        <TextField
          placeholder="Search by email..."
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          size="small"
          sx={{
            minWidth: 300,
            '& .MuiOutlinedInput-root': {
              color: theme.textColor,
              '& fieldset': {
                borderColor: theme.borderColor,
              },
              '&:hover fieldset': {
                borderColor: theme.primaryColor,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.primaryColor,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.textSecondaryColor }} />
              </InputAdornment>
            ),
          }}
        />
        
        <Tooltip title="Refresh data">
          <IconButton
            onClick={() => fetchFeedbackData(currentPage)}
            disabled={loading}
            sx={{
              color: theme.textSecondaryColor,
              '&:hover': {
                color: theme.primaryColor,
              },
              '&:disabled': {
                color: theme.textSecondaryColor,
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      
      <Card sx={{ 
        background: theme.cardBackground,
        border: `1px solid ${theme.borderColor}`,
        boxShadow: '0 4px 24px 0 rgba(30,34,40,0.08)'
      }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: theme.backgroundColor }}>
                  <TableCell sx={{ 
                    color: theme.textColor, 
                    fontWeight: 600, 
                    borderBottom: `1px solid ${theme.borderColor}`,
                    py: 2
                  }}>
                    Report ID
                  </TableCell>
                  <TableCell sx={{ 
                    color: theme.textColor, 
                    fontWeight: 600, 
                    borderBottom: `1px solid ${theme.borderColor}`,
                    py: 2
                  }}>
                    Email
                  </TableCell>
                  <TableCell sx={{ 
                    color: theme.textColor, 
                    fontWeight: 600, 
                    borderBottom: `1px solid ${theme.borderColor}`,
                    py: 2
                  }}>
                    Rating
                  </TableCell>
                  <TableCell sx={{ 
                    color: theme.textColor, 
                    fontWeight: 600, 
                    borderBottom: `1px solid ${theme.borderColor}`,
                    py: 2
                  }}>
                    Feedback Text
                  </TableCell>
                  <TableCell sx={{ 
                    color: theme.textColor, 
                    fontWeight: 600, 
                    borderBottom: `1px solid ${theme.borderColor}`,
                    py: 2
                  }}>
                    Date/Time
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, color: theme.textSecondaryColor }}>
                      <Typography variant="body1">Loading feedback data...</Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, color: theme.textSecondaryColor }}>
                      <Typography variant="body1">
                        {searchEmail ? 'No feedback found matching your search.' : 'No feedback data available.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData.map((feedback, index) => (
                    <TableRow 
                      key={feedback.id}
                      sx={{
                        '&:hover': {
                          background: theme.backgroundColor,
                        },
                        '&:last-child td': {
                          borderBottom: 'none',
                        },
                      }}
                    >
                      <TableCell sx={{ 
                        color: theme.textColor,
                        borderBottom: `1px solid ${theme.borderColor}`,
                        py: 2
                      }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500,
                            color: theme.primaryColor,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            '&:hover': {
                              color: theme.primaryColor,
                              opacity: 0.8
                            }
                          }}
                          onClick={() => handleReportIdClick(feedback.id)}
                        >
                          {feedback.id}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ 
                        color: theme.textColor,
                        borderBottom: `1px solid ${theme.borderColor}`,
                        py: 2
                      }}>
                        <Typography variant="body2">
                          {feedback.email}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ 
                        color: theme.textColor,
                        borderBottom: `1px solid ${theme.borderColor}`,
                        py: 2
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {renderStars(feedback.rating)}
                          </Box>
                          <Chip
                            label={feedback.rating}
                            size="small"
                            sx={{
                              background: getRatingColor(feedback.rating) + '20',
                              color: getRatingColor(feedback.rating),
                              fontWeight: 600,
                              ml: 1
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ 
                        color: theme.textColor,
                        borderBottom: `1px solid ${theme.borderColor}`,
                        py: 2,
                        maxWidth: 300
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {countWords(feedback.feedback) > 10 ? (
                              <>
                                {expandedRows.has(feedback.id) ? feedback.feedback : truncateText(feedback.feedback)}
                                <IconButton
                                  size="small"
                                  onClick={() => toggleRowExpansion(feedback.id)}
                                  sx={{
                                    color: theme.primaryColor,
                                    ml: 1,
                                    p: 0.5,
                                    '&:hover': {
                                      background: theme.primaryColor + '10',
                                    },
                                  }}
                                >
                                  {expandedRows.has(feedback.id) ? 
                                    <ExpandLessIcon sx={{ fontSize: 16 }} /> : 
                                    <ExpandMoreIcon sx={{ fontSize: 16 }} />
                                  }
                                </IconButton>
                              </>
                            ) : (
                              feedback.feedback
                            )}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ 
                        color: theme.textSecondaryColor,
                        borderBottom: `1px solid ${theme.borderColor}`,
                        py: 2
                      }}>
                        <Typography variant="body2">
                          {feedback.date}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Pagination Controls */}
      {pagination.total_pages > 1 && (
        <Box sx={{ 
          mt: 3, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          gap: 2
        }}>
          <Button
            variant="outlined"
            startIcon={<ChevronLeftIcon />}
            onClick={() => fetchFeedbackData(currentPage - 1)}
            disabled={!pagination.has_prev || loading}
            sx={{
              borderColor: theme.borderColor,
              color: theme.textColor,
              '&:hover': {
                borderColor: theme.primaryColor,
                background: `${theme.primaryColor}10`,
              },
              '&:disabled': {
                borderColor: theme.textSecondaryColor,
                color: theme.textSecondaryColor,
              },
            }}
          >
            Previous
          </Button>
          
          <Typography variant="body2" sx={{ color: theme.textSecondaryColor, mx: 2 }}>
            Page {currentPage} of {pagination.total_pages}
          </Typography>
          
          <Button
            variant="outlined"
            endIcon={<ChevronRightIcon />}
            onClick={() => fetchFeedbackData(currentPage + 1)}
            disabled={!pagination.has_next || loading}
            sx={{
              borderColor: theme.borderColor,
              color: theme.textColor,
              '&:hover': {
                borderColor: theme.primaryColor,
                background: `${theme.primaryColor}10`,
              },
              '&:disabled': {
                borderColor: theme.textSecondaryColor,
                color: theme.textSecondaryColor,
              },
            }}
          >
            Next
          </Button>
        </Box>
      )}

      {/* Report Details Modal */}
      <Modal
        open={modalOpen}
        onClose={handleModalClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={modalOpen}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '90%', md: '85%', lg: '80%' },
            maxWidth: 1200,
            maxHeight: '90vh',
            overflow: 'auto',
            bgcolor: theme.cardBackground,
            border: `1px solid ${theme.borderColor}`,
            borderRadius: 2,
            boxShadow: 24,
            p: 0,
          }}>
            {reportLoading ? (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: 200,
                color: theme.textColor 
              }}>
                <CircularProgress size={40} sx={{ color: theme.primaryColor }} />
                <Typography variant="body1" sx={{ ml: 2 }}>
                  Loading report details...
                </Typography>
              </Box>
            ) : selectedReport ? (
              <>
                {/* Modal Header */}
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 3,
                  borderBottom: `1px solid ${theme.borderColor}`,
                  bgcolor: theme.backgroundColor
                }}>
                  <Typography variant="h5" sx={{ 
                    color: theme.textColor,
                    fontWeight: 'bold'
                  }}>
                    Report Content
                  </Typography>
                  <IconButton
                    onClick={handleModalClose}
                    sx={{
                      color: theme.textSecondaryColor,
                      '&:hover': {
                        color: theme.textColor,
                        bgcolor: theme.borderColor
                      }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>

                {/* Modal Content - Only Report */}
                <Box sx={{ p: 3 }}>
                  <Box
                    sx={{
                      bgcolor: theme.backgroundColor,
                      border: `1px solid ${theme.borderColor}`,
                      borderRadius: 1,
                      p: 3,
                      minHeight: 200,
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        color: theme.textColor,
                        marginBottom: '16px',
                        marginTop: '16px',
                        fontWeight: 600
                      },
                      '& p': {
                        color: theme.textColor,
                        lineHeight: 1.6,
                        marginBottom: '12px'
                      },
                      '& ul, & ol': {
                        color: theme.textColor,
                        paddingLeft: '20px',
                        marginBottom: '12px'
                      },
                      '& li': {
                        marginBottom: '4px',
                        lineHeight: 1.5
                      },
                      '& strong, & b': {
                        fontWeight: 600,
                        color: theme.textColor
                      },
                      '& em, & i': {
                        fontStyle: 'italic'
                      },
                      '& blockquote': {
                        borderLeft: `4px solid ${theme.primaryColor}`,
                        paddingLeft: '16px',
                        margin: '16px 0',
                        fontStyle: 'italic',
                        color: theme.textSecondaryColor
                      },
                      '& pre': {
                        backgroundColor: theme.cardBackground,
                        border: `1px solid ${theme.borderColor}`,
                        borderRadius: '4px',
                        padding: '12px',
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '14px'
                      },
                      '& code': {
                        backgroundColor: theme.cardBackground,
                        padding: '2px 4px',
                        borderRadius: '3px',
                        fontFamily: 'monospace',
                        fontSize: '14px'
                      },
                      '& table': {
                        width: '100%',
                        borderCollapse: 'collapse',
                        marginBottom: '16px'
                      },
                      '& th, & td': {
                        border: `1px solid ${theme.borderColor}`,
                        padding: '8px 12px',
                        textAlign: 'left'
                      },
                      '& th': {
                        backgroundColor: theme.backgroundColor,
                        fontWeight: 600
                      },
                      '& a': {
                        color: theme.primaryColor,
                        textDecoration: 'underline',
                        '&:hover': {
                          opacity: 0.8
                        }
                      },
                      '& hr': {
                        border: 'none',
                        borderTop: `1px solid ${theme.borderColor}`,
                        margin: '20px 0'
                      }
                    }}
                    dangerouslySetInnerHTML={{
                      __html: selectedReport.report || '<p style="color: #999;">No report content available</p>'
                    }}
                  />
                </Box>
              </>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: 200,
                color: theme.textColor 
              }}>
                <Typography variant="body1">
                  No report data available
                </Typography>
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>
    </Box>
  );
}
